import type { PoolLedger, PoolPosition } from '@myrewards/contracts';
import { useMemo, useState, type ReactElement, type RefObject } from 'react';
import { ledgerExportHref } from '../api/settings';
import { Avatar } from '../acting-for/parts';
import {
  Card,
  EmptyState,
  Icon,
  StatusPill,
  buttonClassName,
  cx,
  formatCount,
  formatDate,
  formatMoney,
  plural,
  type StatusTone,
} from '../ui';
import { LEDGER_ANCHOR, POOLS_ANCHOR } from './budget-model';
import {
  LEDGER_KIND_LABEL,
  ledgerFilterOf,
  ledgerSummary,
  mergeLedgers,
  signedMoney,
  signedOptions,
  type LedgerFilter,
} from './model';
import { ChangedLine, ScrollBox } from './parts';
import s from './Settings.module.css';

/**
 * Budget pools' ledger card (X4, K31): every entry this fiscal year, narrowed by pool and kind,
 * with who made each one and, when a Stock Admin made it for an Executive, who it was for
 * (APP-148 D21). Exports what's shown as CSV.
 */

type Pool = PoolPosition;

/**
 * "Note and 1 file on the award" (Ledger board, D21): what a change done for someone carries,
 * shown to those who may read it (`actedFor`); null when it carries neither.
 */
function carriedWords(
  actedFor: NonNullable<PoolLedger['entries'][number]['actedFor']>,
  onAward: boolean,
): string | null {
  const where = onAward ? 'on the award' : 'with the change';
  const files = plural(actedFor.files, 'file', 'files');
  if (actedFor.hasNote && actedFor.files > 0) return `Note and ${files} ${where}`;
  if (actedFor.hasNote) return `Note ${where}`;
  if (actedFor.files > 0) return `${files.charAt(0).toUpperCase()}${files.slice(1)} ${where}`;
  return null;
}

/** The ledger's chips: every entry, those done for someone (APP-148 D21), then each kind. */
type LedgerChip = LedgerFilter | 'for_someone';

const FILTERS: readonly { id: LedgerChip; label: string }[] = [
  { id: 'all', label: 'All entries' },
  { id: 'for_someone', label: 'Done for someone else' },
  { id: 'initial', label: 'Initial budget' },
  { id: 'addition', label: 'Addition' },
  { id: 'reduction', label: 'Reduction' },
  { id: 'move', label: 'Move' },
  { id: 'top_up', label: 'Top-up for over-budget' },
];

const KIND_TONE: Readonly<Record<Exclude<LedgerFilter, 'all'>, StatusTone>> = {
  initial: 'neutral',
  addition: 'done',
  reduction: 'problem',
  move: 'info',
  top_up: 'waiting',
};

export function LedgerCard({
  pools,
  ledgers,
  fiscalYear,
  loading,
  previewing,
  canSet,
  headingRef,
}: {
  pools: readonly Pool[];
  ledgers: readonly PoolLedger[];
  fiscalYear: number;
  loading: boolean;
  /** Preview has no live ledger, so nothing to export. */
  previewing: boolean;
  /** This person sets budgets and none is set, so the empty ledger leads to the pools. */
  canSet: boolean;
  headingRef: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  const [poolFilter, setPoolFilter] = useState<string | null>(null);
  const [kind, setKind] = useState<LedgerChip>('all');
  const rows = useMemo(() => mergeLedgers(ledgers), [ledgers]);
  const inPool = rows.filter((r) => poolFilter === null || r.pool.id === poolFilter);
  const matches = (r: (typeof rows)[number], chip: LedgerChip): boolean =>
    chip === 'all'
      ? true
      : chip === 'for_someone'
        ? r.entry.for != null
        : ledgerFilterOf(r.entry.kind) === chip;
  const shown = inPool.filter((r) => matches(r, kind));
  const latest = rows[0];
  const allTotal = pools.reduce((n, p) => n + (p.budget?.cashCents ?? 0), 0);
  const allNet = ledgers.reduce((n, l) => n + ledgerSummary(l).netCents, 0);
  // Nothing to narrow yet: the pool tiles and kind chips wait for the first entry (APP-093).
  const noEntries = !loading && rows.length === 0;

  return (
    <Card
      id={LEDGER_ANCHOR}
      headingRef={headingRef}
      title={`Budget ledger · FY${String(fiscalYear)}`}
      helpAnchor="budget-ledger"
      help={{
        shows:
          'Every change to every pool’s budget this fiscal year: initial budgets, additions, reductions, moves and over-budget top-ups, with who and why.',
        canDo:
          'Pick a pool or a kind of change to narrow the list, and export what’s shown as CSV. Entries are never edited or deleted; a correction is a new entry.',
      }}
      {...(!previewing &&
        rows.length > 0 && {
          aside: (
            // The entries shown, as CSV with each one's time (UTC), source and batch (X4); under
            // "Done for someone else", only those (APP-148 R74).
            <a
              href={ledgerExportHref({
                fiscalYear,
                poolId: poolFilter,
                change: kind === 'all' || kind === 'for_someone' ? null : kind,
                doneForSomeone: kind === 'for_someone',
              })}
              download
              className={buttonClassName({ variant: 'ghost', size: 'sm' })}
              aria-label={`Export ${plural(shown.length, 'ledger entry', 'ledger entries')} as CSV`}
            >
              <Icon name="download" size={16} />
              Export
            </a>
          ),
        })}
    >
      <ChangedLine
        verb="Last entry"
        change={
          latest === undefined
            ? null
            : // An entry with no person was made by the deployment's starting setup (APP-118).
              {
                by: latest.entry.by ?? { name: 'MyRewards setup' },
                at: latest.entry.at,
                for: latest.entry.for ?? null,
              }
        }
        empty={loading ? 'Loading the ledger…' : `No entries yet for FY${String(fiscalYear)}`}
      />
      {!noEntries && (
        <div className={s.tiles} role="group" aria-label="Show the ledger for">
          <button
            type="button"
            className={cx(s.tile, poolFilter === null && s.tileOn)}
            aria-pressed={poolFilter === null}
            onClick={() => {
              setPoolFilter(null);
            }}
          >
            <span className={cx('eyebrow', s.tileLabel)}>All pools</span>
            <span className={cx('disp', s.tileValue)}>{formatMoney(allTotal)}</span>
            <span className={s.tileMeta}>
              {plural(rows.length, 'entry', 'entries')} · net {signedMoney(allNet)}
            </span>
          </button>
          {pools.map((p) => {
            const l = ledgers.find((x) => x.pool.id === p.pool.id);
            const sum = l === undefined ? null : ledgerSummary(l);
            return (
              <button
                key={p.pool.id}
                type="button"
                className={cx(s.tile, poolFilter === p.pool.id && s.tileOn)}
                aria-pressed={poolFilter === p.pool.id}
                onClick={() => {
                  setPoolFilter(p.pool.id);
                }}
              >
                <span className={cx('eyebrow', s.tileLabel)}>{p.pool.name}</span>
                <span className={cx('disp', s.tileValue)}>
                  {p.budget === null ? 'Not set' : formatMoney(p.budget.cashCents)}
                </span>
                <span className={s.tileMeta}>
                  {sum === null
                    ? 'Loading…'
                    : sum.startedCents === null
                      ? 'No entries yet'
                      : sum.netCents === 0
                        ? `Started ${formatMoney(sum.startedCents)} · no changes`
                        : `Started ${formatMoney(sum.startedCents)} · net ${signedMoney(sum.netCents)}`}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {!noEntries && (
        <div className={s.chips} role="group" aria-label="Kind of change">
          {FILTERS.map((f) => {
            const count = inPool.filter((r) => matches(r, f.id)).length;
            return (
              <button
                key={f.id}
                type="button"
                className={cx(s.chip, kind === f.id && s.chipOn)}
                aria-pressed={kind === f.id}
                onClick={() => {
                  setKind(f.id);
                }}
              >
                {f.label} · {count}
              </button>
            );
          })}
          <span className={s.chipNote} aria-live="polite">
            {plural(shown.length, 'entry', 'entries')}
          </span>
        </div>
      )}
      {loading && rows.length === 0 ? (
        <p className={s.muted}>Loading the ledger…</p>
      ) : (
        <ScrollBox label="Ledger entries">
          <table className={cx(s.table, s.ledgerTable)}>
            <caption className="visually-hidden">
              Budget ledger entries for {fiscalYear}, newest first
            </caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Pool</th>
                <th scope="col">Change</th>
                <th scope="col" className={s.num}>
                  Amount
                </th>
                <th scope="col" className={s.num}>
                  Pool budget after
                </th>
                <th scope="col">Who</th>
                <th scope="col">Reason</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                // The header row stays, with one row holding the designed empty state (APP-093).
                <tr className={s.emptyStateRow}>
                  <td colSpan={7}>
                    {rows.length === 0 ? (
                      <EmptyState
                        variant="first-day"
                        art="chart"
                        headline="The ledger starts with your first budget"
                        body={`When the ${String(fiscalYear)} pools are set, each starting budget lands here as Initial budget. Every addition, reduction, move and top-up after that follows, with who made it and why, ready for year-end reporting.`}
                        {...(canSet && {
                          action: { label: 'Set the budget pools', href: `#${POOLS_ANCHOR}` },
                        })}
                      />
                    ) : (
                      <EmptyState
                        variant="first-day"
                        art="search"
                        size="sm"
                        headline="No entries match"
                        body="Pick another pool or kind of change, or choose All to see every entry."
                      />
                    )}
                  </td>
                </tr>
              ) : (
                shown.map((r) => {
                  const f = ledgerFilterOf(r.entry.kind);
                  return (
                    <tr key={r.id} className={cx(r.entry.voided && s.voided)}>
                      <td className={s.nowrap}>{formatDate(r.entry.at)}</td>
                      <td>
                        <span className={s.strong}>{r.pool.name}</span>
                        {r.entry.otherPool !== null && (
                          <span className={s.sub}>
                            {r.entry.kind === 'move_out' ? 'to' : 'from'} {r.entry.otherPool.name}
                          </span>
                        )}
                        {r.entry.award !== null && (
                          <span className={s.sub}>For {r.entry.award.displayId ?? 'an award'}</span>
                        )}
                      </td>
                      <td>
                        <StatusPill
                          tone={KIND_TONE[f]}
                          size="sm"
                          label={`${LEDGER_KIND_LABEL[r.entry.kind]}${r.entry.voided ? ' · deleted' : ''}`}
                        />
                      </td>
                      <td
                        className={cx(
                          s.num,
                          r.entry.cashCents > 0 && s.up,
                          r.entry.cashCents < 0 && s.down,
                        )}
                      >
                        <span className={s.strong}>
                          {r.entry.kind === 'initial'
                            ? formatMoney(r.entry.cashCents)
                            : signedMoney(r.entry.cashCents)}
                        </span>
                        {r.entry.options !== 0 && (
                          <span className={s.sub}>
                            {r.entry.kind === 'initial'
                              ? `${formatCount(r.entry.options)} options`
                              : signedOptions(r.entry.options)}
                          </span>
                        )}
                      </td>
                      <td className={s.num}>
                        <span className={s.strong}>{formatMoney(r.after.cashCents)}</span>
                        <span className={s.sub}>{formatCount(r.after.options)} options</span>
                      </td>
                      <td>
                        <span className={s.who}>
                          <span>{r.entry.by?.name ?? 'MyRewards setup'}</span>
                          {r.entry.for != null && (
                            <span className={s.forPill}>
                              <Avatar name={r.entry.for.name} />
                              <span>for {r.entry.for.name}</span>
                            </span>
                          )}
                          {r.entry.actedFor != null &&
                            carriedWords(r.entry.actedFor, r.entry.award !== null) !== null && (
                              <span className={s.sub}>
                                {carriedWords(r.entry.actedFor, r.entry.award !== null)}
                              </span>
                            )}
                        </span>
                      </td>
                      <td>{r.entry.reason ?? <span className={s.muted}>No reason given</span>}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </ScrollBox>
      )}
      <p className={s.footnote}>
        Pool budget after is that pool’s running total once the entry is applied. A move is one
        entry on each pool. Exports add the time (UTC), source and batch for each entry.
      </p>
    </Card>
  );
}
